import * as R from "ramda"

import { get } from "../../utils/get"
import set from "../../utils/set"
import type { Flight, Flights, JourneyInput, SupportedJourneyHiddenTypes } from "./types"
import { HIDING_REASON_TYPES } from "../../consts/journey"

// We need to have possibility to inform users about hidden_flights
// Hidden flights are used in particular search combination
// For now the only cases we must communicate are return direct flights (marked as one-way)
// ===

export const SUPPORTED_JOURNEY_HIDDEN_TYPES = {
  // One Way: Direct
  // (from return made one way)
  // A -> B USED
  // B -> A throw away
  ONE_WAY_DIRECT: "ONE_WAY_DIRECT",

  // One Way: Direct
  // A -> B USED
  // B -> C throw away
  TRUE_HIDDEN_CITY: "TRUE_HIDDEN_CITY",

  // One Way: Indirect
  // (from return made one way)
  // A -> B USED
  // B -> C throw away
  // C -> D throw away
  // ......   throw away
  // X -> A throw away
  ONE_WAY_INDIRECT: "ONE_WAY_INDIRECT",

  // Return: Direct
  // (aka cross)
  // A1 -> B1, B1 -> A1
  // A2 -> B2, B2 -> A2
  // A1 -> B1, B2 -> A2 + vice versa
  RETURN_DIRECT: "RETURN_DIRECT",
} as const

// For sorting flights by departure time
const flightsDepartureTimeComparator = (a: Flight, b: Flight): number => a.dtime_utc - b.dtime_utc

const flightsFormReturnTrip = (flights: Flights): boolean => {
  const source = R.head(flights)?.src
  const destination = R.last(flights)?.dst

  return flights.length > 1 && source === destination && !R.isNil(source) && !R.isNil(destination)
}

export const determineTypeOfHiddenFlights = (
  flights: Flights,
  hasHiddenCity: boolean,
): SupportedJourneyHiddenTypes => {
  // @ts-expect-error: Flow to TS migration.
  const [validFlights, hiddenFlights] = R.pipe(
    // @ts-expect-error: Flow to TS migration.
    R.partition((flight) => R.isNil(flight.hiding_reason)),
    R.map(R.sort(flightsDepartureTimeComparator)),
    // @ts-expect-error: Flow to TS migration.
  )(flights)

  const validFlightsCount = validFlights.length
  const hiddenFlightsCount = hiddenFlights.length
  if (validFlightsCount === 0 || hiddenFlightsCount === 0) return null

  if (hasHiddenCity) return SUPPORTED_JOURNEY_HIDDEN_TYPES.TRUE_HIDDEN_CITY

  if (validFlightsCount === 1 && hiddenFlightsCount === 1)
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_DIRECT

  // Hidden flights form regular trip (aka segment)
  // https://monosnap.com/file/CWSlToul2fVIAXeDra5BbBb3xMjYNp
  if (hiddenFlightsCount > 0 && flightsFormReturnTrip(validFlights)) {
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.RETURN_DIRECT
  }

  if (
    (validFlightsCount >= 1 && hiddenFlightsCount > 1) ||
    (flights.length === 3 && validFlightsCount >= 1 && hiddenFlightsCount >= 1)
  )
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_INDIRECT

  return null
}

// Combine valid and hidden flights in correct order for FE usage
const flightsCombiner = (flights: Flights, type: SupportedJourneyHiddenTypes): Flights => {
  // FIXME: a method has been detected that possibly mutates the object, consider a more functional
  // approach (Ramda might help)

  const flightsSorted = flights.sort(flightsDepartureTimeComparator)
  const validFlights = flightsSorted.filter((x) => R.isNil(x.hiding_reason))
  const hiddenFlights = flightsSorted.filter(
    (x) =>
      x.hiding_reason === HIDING_REASON_TYPES.TAT || x.hiding_reason === HIDING_REASON_TYPES.THC,
  )

  switch (type) {
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_INDIRECT:
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_DIRECT:
      // @ts-expect-error: Flow to TS migration.
      return [...validFlights, ...hiddenFlights.map(set("return", 1))]
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.RETURN_DIRECT:
      return [...flightsSorted.filter((x) => !x.return), ...flightsSorted.filter((x) => x.return)]
    default:
      return flights
  }
}

export const getFlightsFromInputSafe = (input: JourneyInput): Flights =>
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  input.flights || input.route || []

export const hasThrowawayTicketOrHiddenCity = (input: JourneyInput): boolean =>
  R.compose(
    (reason) =>
      // @ts-expect-error: Flow to TS migration.
      R.any(R.equals(HIDING_REASON_TYPES.TAT))(reason) ||
      // @ts-expect-error: Flow to TS migration.
      R.any(R.equals(HIDING_REASON_TYPES.THC))(reason),
    R.map(get("hiding_reason")),
    getFlightsFromInputSafe,
  )(input)

export const hasHiddenCity = (input: JourneyInput): boolean =>
  // @ts-expect-error: Flow to TS migration.
  R.compose(
    R.any(R.equals(HIDING_REASON_TYPES.THC)),
    R.map(get("hiding_reason")),
    getFlightsFromInputSafe,
  )(input)

export const filterHiddenFlightOut = (rawInput: JourneyInput): JourneyInput => {
  const input = { ...rawInput } as const

  if (input.flights) {
    return set(
      "flights",
      input.flights.filter((flight) => R.isNil(flight.hiding_reason)),
      input,
    )
  }
  if (input.route) {
    return set(
      "route",
      input.route.filter((flight) => R.isNil(flight.hiding_reason)),
      input,
    )
  }

  return input
}

export const hiddenFlightFormattingSupported = (input: JourneyInput): boolean => {
  const flights = getFlightsFromInputSafe(input)
  return Boolean(determineTypeOfHiddenFlights(flights, hasThrowawayTicketOrHiddenCity(input)))
}

// Label second flight as return to simulate correct appearance
export const formatForHiddenFlightsMapping = (rawInput: JourneyInput): JourneyInput => {
  const input = { ...rawInput } as const
  const flights = getFlightsFromInputSafe(input)
  const flightsParamName = input.flights ? "flights" : "route"
  const hiddenFlightsType = determineTypeOfHiddenFlights(flights, hasHiddenCity(input))

  // @ts-expect-error: Flow to TS migration.
  return R.compose(
    set("hiddenFlightsType", hiddenFlightsType),
    set([flightsParamName], flightsCombiner(flights, hiddenFlightsType)),
  )(input)
}
